import React, { useEffect, useState } from 'react';
import { api } from '@/lib/db';
import { useToast } from '@/contexts/ToastContext';
import { Card, Button, Input } from '@/components/ui/Components';
import SafeIcon from '@/common/SafeIcon';

const SmtpSettings = () => {
  const { addToast } = useToast();
  const [config, setConfig] = useState({ smtp_host: '', smtp_port: '587', smtp_user: '', smtp_pass: '', smtp_secure: 'false', smtp_from_email: '' });
  const [testEmail, setTestEmail] = useState('');
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [passSet, setPassSet] = useState(false);

  useEffect(() => {
    api.getConfig().then(data => {
      setConfig(prev => ({
        ...prev,
        smtp_host: data.smtp_host || '',
        smtp_port: data.smtp_port || '587',
        smtp_user: data.smtp_user || '',
        smtp_secure: data.smtp_secure || 'false',
        smtp_from_email: data.smtp_from_email || '',
      }));
      setPassSet(data.smtp_pass_set || false);
    });
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...config };
      if (!payload.smtp_pass) delete payload.smtp_pass; // Üres esetén ne írja felül
      await api.updateConfig(payload);
      addToast('SMTP beállítások mentve!', 'success');
      setPassSet(!!config.smtp_pass || passSet);
      setConfig(prev => ({ ...prev, smtp_pass: '' }));
    } catch (err) {
      addToast(err.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (!testEmail) { addToast('Add meg a teszt e-mail címét', 'error'); return; }
    setTesting(true);
    try {
      await api.testEmail(testEmail);
      addToast(`Teszt e-mail elküldve: ${testEmail}`, 'success');
    } catch (err) {
      addToast(`Hiba: ${err.message}`, 'error');
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div>
        <h1 className="text-3xl font-black text-gray-900">E-mail beállítások</h1>
        <p className="text-gray-500">SMTP konfiguráció a negatív visszajelzés értesítésekhez.</p>
      </div>

      <Card className="p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
          <SafeIcon name="Mail" className="w-5 h-5 text-blue-600" /> SMTP szerver beállítások
        </h3>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 text-sm text-blue-800">
          <p className="font-semibold mb-1">Hogyan működik?</p>
          <p>Ha be van állítva az SMTP, a rendszer automatikusan e-mailt küld a vállalkozás értesítési e-mail címére, ha negatív visszajelzés (1-3 csillag) érkezik.</p>
        </div>
        <form onSubmit={handleSave} className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <div className="col-span-2">
              <Input
                label="SMTP szerver (host)"
                placeholder="smtp.gmail.com"
                value={config.smtp_host}
                onChange={e => setConfig({ ...config, smtp_host: e.target.value })}
              />
            </div>
            <Input
              label="Port"
              type="number"
              placeholder="587"
              value={config.smtp_port}
              onChange={e => setConfig({ ...config, smtp_port: e.target.value })}
            />
          </div>
          <Input
            label="SMTP felhasználónév"
            placeholder="emailit vagy noreply@domain.hu"
            value={config.smtp_user}
            onChange={e => setConfig({ ...config, smtp_user: e.target.value })}
          />
          <div>
            <Input
              label="Feladó e-mail cím *"
              type="email"
              placeholder="noreply@socialcard.hu"
              value={config.smtp_from_email}
              onChange={e => setConfig({ ...config, smtp_from_email: e.target.value })}
            />
            <p className="text-xs text-gray-400 mt-1">Ez jelenik meg a levél „Feladó" mezőjében. Az Emailit-nél a verified sender email-t add meg.</p>
          </div>
          <div>
            <Input
              label={passSet ? "SMTP jelszó (hagyd üresen ha nem változtatod)" : "SMTP jelszó"}
              type="password"
              placeholder={passSet ? "••••••••" : "App jelszó vagy SMTP jelszó"}
              value={config.smtp_pass}
              onChange={e => setConfig({ ...config, smtp_pass: e.target.value })}
            />
            {passSet && <p className="text-xs text-green-600 mt-1">✓ Jelszó be van állítva</p>}
          </div>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={config.smtp_secure === 'true'}
                onChange={e => setConfig({ ...config, smtp_secure: e.target.checked ? 'true' : 'false' })}
                className="rounded"
              />
              <span className="text-sm text-gray-700">SSL/TLS használata (port 465)</span>
            </label>
          </div>
          <div className="flex justify-end">
            <Button type="submit" disabled={saving}>
              {saving ? 'Mentés...' : 'Beállítások mentése'}
            </Button>
          </div>
        </form>
      </Card>

      <Card className="p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
          <SafeIcon name="Send" className="w-5 h-5 text-green-600" /> Teszt e-mail küldése
        </h3>
        <p className="text-sm text-gray-500 mb-4">Ellenőrizd, hogy az SMTP konfiguráció működik-e.</p>
        <div className="flex gap-3">
          <Input
            type="email"
            placeholder="test@domain.hu"
            value={testEmail}
            onChange={e => setTestEmail(e.target.value)}
          />
          <Button variant="outline" onClick={handleTest} disabled={testing}>
            {testing ? 'Küldés...' : 'Teszt küldése'}
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default SmtpSettings;
